import { InvoiceBillingStatusesSchema, InvoiceDateSchema, InvoiceReviewStatusesSchema } from '@weft/types';
import { parseJson, oz } from '@weft/utils';
import type { ZapiAction, ZapiParam } from '@weft/zapi-types';
import { PAGINATION_QUERY_PARAMETERS, SEARCH_QUERY_PARAMETER } from '../../../api-definition-params';
import { GET_INVOICES_OVERVIEWS_PATH } from './get-invoices-overviews-path';
import { GetInvoicesOverviewsResponseSchema } from './get-invoices-overviews-response-schema';

const CUSTOMER_ID_QUERY_PARAMETER = {
  name: 'customerId',
  location: 'query',
  schema: oz.string().optional(),
  required: false,
} as const satisfies ZapiParam;

const DATE_QUERY_PARAMETER = {
  name: 'dates',
  location: 'query',
  schema: oz.string()
    .transform(s => parseJson<string[]>(s))
    .pipe(InvoiceDateSchema.array()).optional(),
  required: false,
} as const satisfies ZapiParam;

const INVOICES_REVIEWS_STATUSES_QUERY_PARAMETER = {
  name: 'reviewStatuses',
  location: 'query',
  schema: InvoiceReviewStatusesSchema.optional(),
  required: false,
} as const satisfies ZapiParam;

const INVOICES_BILLING_STATUSES_QUERY_PARAMETER = {
  name: 'billingStatuses',
  location: 'query',
  schema: InvoiceBillingStatusesSchema.optional(),
  required: false,
} as const satisfies ZapiParam;

export const INVOICES_OVERVIEWS_ADDITIONAL_FILTERS = Object.freeze({
  Overage: 'overage',
});

export const InvoicesOverviewsAdditionalFilterSchema = oz.nativeEnum(INVOICES_OVERVIEWS_ADDITIONAL_FILTERS);
export const InvoicesOverviewsAdditionalFiltersSchema = oz.string()
  .transform(s => parseJson<InvoicesOverviewsAdditionalFilter[]>(s))
  .pipe(InvoicesOverviewsAdditionalFilterSchema.array()).optional();

export type InvoicesOverviewsAdditionalFilter = (typeof INVOICES_OVERVIEWS_ADDITIONAL_FILTERS)[keyof typeof INVOICES_OVERVIEWS_ADDITIONAL_FILTERS];

const ADDITIONAL_FILTERS_PARAMETER = {
  name: 'additionalFilters',
  location: 'query',
  schema: InvoicesOverviewsAdditionalFiltersSchema,
  required: false,
} as const satisfies ZapiParam;

export const GET_INVOICES_OVERVIEWS_ACTION = {
  method: 'get',
  codeGenerationSettings: {
    actionText: 'get-invoices-overviews',
  },
  queryParams: {
    ...PAGINATION_QUERY_PARAMETERS,
    [SEARCH_QUERY_PARAMETER.name]: SEARCH_QUERY_PARAMETER,
    [CUSTOMER_ID_QUERY_PARAMETER.name]: CUSTOMER_ID_QUERY_PARAMETER,
    [DATE_QUERY_PARAMETER.name]: DATE_QUERY_PARAMETER,
    [INVOICES_REVIEWS_STATUSES_QUERY_PARAMETER.name]: INVOICES_REVIEWS_STATUSES_QUERY_PARAMETER,
    [INVOICES_BILLING_STATUSES_QUERY_PARAMETER.name]: INVOICES_BILLING_STATUSES_QUERY_PARAMETER,
    [ADDITIONAL_FILTERS_PARAMETER.name]: ADDITIONAL_FILTERS_PARAMETER,
  },
  response: {
    name: 'GetInvoicesOverviewsResponse',
    schema: GetInvoicesOverviewsResponseSchema,
  },
  path: GET_INVOICES_OVERVIEWS_PATH,
} satisfies ZapiAction;

export type GetInvoicesOverviewsAction = typeof GET_INVOICES_OVERVIEWS_ACTION;